import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
// Helpers
import { convertSecondsToDateFormat } from '@cube3/common/utils/convertSecondsToDateFormat';
// UI
import { SearchField } from './SearchField';
import { Divider } from '../../atoms/Divider/Divider';
import Button from '../../atoms/Button/Button';
import { makeCSS } from '../../../utils/makeCSS';
import { Avatar } from '../../atoms/Avatar/Avatar';
import { SizeVariants } from '../../../theme/themes';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { isMobile } from 'react-device-detect';


const useCSS = makeCSS(({ theme }) => ({
  root: {
    boxSizing: 'border-box',
    border: `1px solid ${theme.color.line['02']}`,
    borderRadius: theme.borderRadius.lg, // 8px
    padding: '0 16px',
    background: theme.color.underlay['01'],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '&:focus-within': {
      boxShadow: !isMobile ? `0px 0px 0px 2px rgba(255, 212, 35, 0.12)` : ''
    }
  },
  column: {
    boxSizing: 'border-box',
    minHeight: '64px',
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  }
}));

interface Props {
  assetType: 'video' | 'image';
  isDrawable: boolean;
  reviewer: string;
  reviewerImage?: string;
  timestamp?: number;
  drawingBarToggled: boolean;
  playing: boolean;
  drawnSvg: string;
  onSend: (message: string, timestamp: number) => void;
  onToggleDrawingBar: () => void;
  playPause?: (override?: boolean) => void;
}

const CommentField: React.FC<Props> = React.memo((props) => {
  const classes = useCSS();
  const [newComment, setNewComment] = useState<string>();
  const [timestampChecked, setTimestampChecked] = useState(true);

  const {
    reviewer,
    reviewerImage,
    isDrawable,
    timestamp,
    drawingBarToggled,
    playing,
    drawnSvg,
    assetType,
    onSend,
    onToggleDrawingBar,
    playPause
  } = props;

  const onChange = useCallback((e) => setNewComment(e.target.value), []);

  const prevCheckboxState = useRef(undefined);
  const showTimestamp = assetType === 'video';


  const onToggleTimestamp = useCallback(
    () =>
      setTimestampChecked((prev) => {
        // if the checkbox is clicked during playback, should pause the video
        if (playing && playPause) {
          playPause(false);
          return true;
        }
        return !prev;
      }),
    [playPause, playing]
  );

  const onClickSend = useCallback(
    (e) => {
      e.preventDefault();
      if (!newComment) {
        return;
      }
      onSend(
        newComment,
        showTimestamp && timestampChecked ? timestamp : undefined
      );
      setNewComment('');
      if (drawingBarToggled) {
        onToggleDrawingBar();
      }
    },
    [
      onSend,
      newComment,
      drawingBarToggled,
      onToggleDrawingBar,
      timestampChecked,
      timestamp,
      showTimestamp
    ]
  );

  useEffect(() => {
    if (drawingBarToggled) {
      setTimestampChecked(true);
    }
  }, [drawingBarToggled]);

  // uncheck the timestamp checkbox during playback
  useEffect(() => {
    if (playing && timestampChecked) {
      setTimestampChecked(false);
      // store the checkbox state
      prevCheckboxState.current = true;
    }
  }, [playing, timestampChecked]);

  // after pause restore the stored checkbox state
  useEffect(() => {
    if (
      !playing &&
      prevCheckboxState.current !== undefined &&
      prevCheckboxState.current !== timestampChecked
    ) {
      setTimestampChecked(prevCheckboxState.current);
      prevCheckboxState.current = undefined; // clear stored checkbox state
    }
  }, [timestampChecked, playing]);

  const timestampText =
    timestamp !== undefined
      ? convertSecondsToDateFormat(timestampChecked ? timestamp : 0)
      : undefined;




  return (
    <form className={`${classes.root}`} onSubmit={onClickSend}
    >
      <div className={classes.column} >
        <SearchField

          value={newComment}
          onChange={onChange}
          placeholder="Add your thoughts..."
          withBorder={false}
          multiline={true}
          maxRows={10}
          startAdornment={
            <Avatar
              size={SizeVariants.sm}
              userInitial={reviewer}
              imageUrl={reviewerImage}
            />
          }
        />
      </div>

      <div style={{ width: 'calc(100% + 32px' }}>
        <Divider direction="horizontal" color="line3" />
      </div>

      <div className={classes.column}>
        {useMemo(() => {
          return showTimestamp && timestamp !== undefined ? (
            <div style={{ opacity: playing ? 0.6 : 1 }}>
              <Checkbox
                size={isMobile ? SizeVariants.md : SizeVariants.sm}
                disabled={drawingBarToggled}
                value={timestampChecked}
                onChange={onToggleTimestamp}
                text={timestampText}
              />
            </div>
          ) : null;
        }, [
          timestampText,
          showTimestamp,
          timestampChecked,
          playing,
          drawingBarToggled
        ])}
        <div className={classes.buttonGroup}>
          {isDrawable && (
            <Button
              data-pw={'iconGesture'}
              iconLeft={'gesture'}
              background="secondary"
              buttonStyle="solid"
              onClick={onToggleDrawingBar}
              size={isMobile ? SizeVariants.md : SizeVariants.sm}
            >
              {drawingBarToggled ? 'Cancel' : 'Draw'}
            </Button>
          )}
          <div style={{ width: 8 }}></div>
          {useMemo(
            () => (
              <Button
                background="primary"
                buttonStyle="solid"
                onClick={onClickSend}
                disabled={!newComment}
                size={isMobile ? SizeVariants.md : SizeVariants.sm}
              >
                Post
              </Button>
            ),
            [newComment, timestampChecked, drawingBarToggled, drawnSvg] // should not re-render when playing a video (timestamp changes)
          )}
        </div>
      </div>
    </form>
  );
});

export default CommentField;
